<template>
  <NavigationLayout :navigationTitle="$t('transferRequest')">
    <div id="mypage__wrapper" slot="contents">
      <div id="mypage__container">
        <!-- 送金申請確認用ダイアログ -->
        <ConfirmDialog
          @finish="onFinishCashConfirmDialog"
          :isActive="cashConfirmDialogIsActive"
          :okButtonText="$t('transfer')"
          :cancelButtonText="$t('buttonCancel')"
          :titleText="cashConfirmDialogTitle"
        />
        <!-- 送金申請失敗用ダイアログ -->
        <ConfirmDialog
          @finish="onFinishCashFailureDialog"
          :isActive="cashFailureDialogIsActive"
          :okButtonText="$t('contactUs')"
          :cancelButtonText="$t('payoneerClose')"
          :messageText="$t('payoneerTransferFailedMessage2')"
          :titleText="$t('payoneerTransferFailedMessage1')"
        />

        <section>
          <ListSubheader :title="$t('loggedInAccount')" />
          <ListItemWithSlots>
            <template #left>{{ $t('nickname') }}</template>
            <template #right>{{ user && user.nickname }}</template>
          </ListItemWithSlots>

          <ListItemWithSlots>
            <template #left>ID</template>
            <template #right>{{ user && user.vliveId }}</template>
          </ListItemWithSlots>

          <ListItemWithSlots>
            <template #left>{{ $t('currentLivePoints') }}</template>
            <template #right>
              <span>
                <img src="/img/livePoint.svg" class="live_point_icon" />{{
                  formattedAmountOfLivePoints
                }}
              </span>
            </template>
          </ListItemWithSlots>
        </section>

        <section class="payout-form__wrapper">
          <div class="payout-form__container">
            <div
              class="list__container"
              v-if="wiseAccountExists"
              :class="{ 'list__container--disabled': !canApplyForRedemption }"
            >
              <ListSubheader :title="$t('livePointsCashTransfer')" />
              <div class="list__description">
                <p>
                  {{
                    $t('livePointDescription', {
                      minLivePoints: formatter.format(minLivePoints),
                    })
                  }}
                </p>
                <p>
                  {{
                    $t('currentExchangeRateDescription', {
                      livePoints: 100,
                      dollars: rateOfUSDDisplay,
                    })
                  }}
                </p>
              </div>

              <ListItemWithSlots>
                <template #left>{{ $t('amount') }}</template>
                <template #right>
                  <div>
                    <img src="/img/livePoint.svg" class="live_point_icon" />
                    <input
                      type="number"
                      class="cash-amount"
                      :placeholder="String($t('EnterAmountMessage'))"
                      :disabled="!canApplyForRedemption"
                      v-model="inputCashAmount"
                      pattern="[0-9]*"
                    />
                  </div>
                </template>
              </ListItemWithSlots>
              <ListItemWithSlots class="list__amount">
                <template #left>{{ $t('transferAmount') }}</template>
                <template #right>${{ resultCash }}</template>
              </ListItemWithSlots>

              <ListSubheader :title="$t('preferredCurrency')" />
              <ListItemWithSlots>
                <template #right>
                  <div>
                    <select
                      v-model="preferredCurrency"
                      class="cash-amount"
                      name="preferredCurrency"
                      :disabled="!canApplyForRedemption"
                      required
                    >
                      <option
                        v-for="(currency, index) in wiseCurrencyList"
                        :key="index"
                        :value="currency"
                      >
                        {{ currency }}
                      </option>
                    </select>
                  </div>
                </template>
              </ListItemWithSlots>
              <div class="list__description">
                <p>
                  {{ $t('noteOnPreferredCurrency') }}
                </p>
              </div>
            </div>

            <div class="error__wrapper" v-if="errorMessage !== ''">
              <div class="error__container">
                <p>{{ errorMessage }}</p>
                <p></p>
              </div>
            </div>

            <div class="button__wrapper" v-if="wiseAccountExists">
              <div class="button__container">
                <PrimaryButton
                  :disabled="!userCanSubmitRequest"
                  @click="onClickCashButton"
                  >{{ $t('transfer') }}</PrimaryButton
                >
              </div>
            </div>
          </div>
        </section>

        <div class="notes__wrapper">
          <div class="notes__container">
            <template v-if="nextRateOfUSD">
              <p>
                {{
                  $t('nextRangeDisplayMessage', {
                    livePoints: 100,
                    dollars: nextRateOfUSDDisplay,
                    date: nextApplicationBeginDateDisplay,
                    prevDate: currentApplicationEndDateDisplay,
                  })
                }}
              </p>
            </template>
            <p>{{ $t('wiseTransferRequestNote01') }}</p>
            <p>{{ $t('wiseTransferRequestNote02') }}</p>
            <p>{{ $t('wiseTransferRequestNote03') }}</p>
            <p>{{ $t('wiseTransferRequestNote04') }}</p>
            <p>
              {{
                $t('wiseTransferRequestNote05', {
                  minLivePoints: formatedMinLivePoints,
                  maxLivePoints: formatedMaxLivePoints,
                })
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import NavigationLayout from '@/components/NavigationLayout.vue'
import ListItemWithSlots from '@/components/ListItemWithSlots.vue'
import ListSubheader from '@/components/ListSubheader.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { StreamerUser } from '@/models'
import { api } from '@/api/appGatewayServerApi'
import { apiV2 } from '@/api/appGatewayServerV2Api'
import { handleUnauthorized } from '@/utils/handle_error'
import { checkInMaintenance } from '@/utils/maintenance'
import Big from 'big.js'
import dayjs, { Dayjs } from 'dayjs'
import { WiseService } from '@/services/wise'
import {
  wisePayoutMinLivePoints,
  wisePayoutMaxLivePoints,
  wiseDefaultPreferredCurrency,
  wiseCurrencyList,
} from '@/models/Wise'

dayjs.extend(require('dayjs/plugin/timezone'))
dayjs.extend(require('dayjs/plugin/utc'))

@Component({
  components: {
    NavigationLayout,
    ListSubheader,
    ListItemWithSlots,
    PrimaryButton,
    ConfirmDialog,
  },
})
export default class WisePayout extends Mixins(LanguageQueryHandler) {
  // LIVEポイント換金の下限定義
  minLivePoints = wisePayoutMinLivePoints

  user: StreamerUser | null = null

  // システム側のLIVEポイント換金可否
  systemCanConvertLivePoints: boolean = false
  // 保有LIVEポイント数
  amountOfLivePoints: number = 0
  // 換金額入力欄の値
  inputCashAmount: string = ''

  // 現在USDレート
  rateOfUSD: Big | null = null
  // 次回USDレート
  nextRateOfUSD: Big | null = null

  // 次回USDレート適用開始日
  nextApplicationBeginDate: Dayjs | null = null

  // 送金処理中フラグ
  isRequestingCash: boolean = false

  // 確認ダイアログ表示フラグ
  cashConfirmDialogIsActive: boolean = false
  // 送金失敗時ダイアログ表示フラグ
  cashFailureDialogIsActive: boolean = false

  formatter = new Intl.NumberFormat('us-En')

  // wiseアカウント登録済みかどうか
  wiseAccountExists = false

  // 受け取り希望通貨（デフォルトUSD）
  preferredCurrency: string = wiseDefaultPreferredCurrency
  // 受け取り希望通貨リスト
  wiseCurrencyList = wiseCurrencyList

  async created() {
    try {
      // アカウント情報の取得
      await this.fetchStreamerUser()
      if (!this.user) return

      // LIVEポイント関連の情報取得
      this.fetchBalanceOfLivePoints()
      this.fetchRate()

      // wiseアカウントチェック
      await this.verifyWiseAccount()
    } catch (e) {
      if (checkInMaintenance(e)) {
        // メンテの場合はそのままメンテ中画面へリダイレクト
        this.$router.push({ path: '/maintenance' }).catch(() => {})
        return
      }
      handleUnauthorized(e)
    }
  }

  // カンマ区切りの保有LIVEポイント数
  get formattedAmountOfLivePoints(): string {
    return this.formatter.format(this.amountOfLivePoints)
  }

  // カンマ区切りの換金可能最低LIVEポイント数
  get formatedMinLivePoints(): string {
    return this.formatter.format(wisePayoutMinLivePoints)
  }

  // カンマ区切りの換金可能上限LIVEポイント数
  get formatedMaxLivePoints(): string {
    return this.formatter.format(wisePayoutMaxLivePoints)
  }

  // USDレート表示用
  get rateOfUSDDisplay(): string {
    return this.rateOfUSD == null ? '-' : this.rateOfUSD.mul(100).toFixed(3)
  }

  // 次回のUSDレート予告表示用
  get nextRateOfUSDDisplay(): string {
    return this.nextRateOfUSD == null
      ? '-'
      : this.nextRateOfUSD.mul(100).toFixed(3)
  }

  // 次回のUSDレート適用開始日表示用
  get nextApplicationBeginDateDisplay(): string {
    return this.nextApplicationBeginDate == null
      ? '-'
      : this.nextApplicationBeginDate.format('YYYY/MM/DD')
  }

  // 現在のUSDレート適用終了日表示用
  get currentApplicationEndDateDisplay(): string {
    return this.nextApplicationBeginDate == null
      ? '-'
      : this.nextApplicationBeginDate.subtract(1, 'day').format('YYYY/MM/DD')
  }

  // 送金額
  get resultCash(): string {
    if (!this.userCanSubmitRequest || this.rateOfUSD == null) {
      return '0'
    }
    const cashed = new WiseService().cash(this.cashAmount, this.rateOfUSD)
    if (cashed.lt(0)) {
      return '0'
    }

    return cashed.toString()
  }

  // 換金申請可能かどうか
  get userCanSubmitRequest(): boolean {
    return (
      this.wiseAccountExists && // wise用アカウント情報が登録されているか
      this.systemCanConvertLivePoints && // システム側がLIVEポイント換金可能な状態か
      this.canCash(this.cashAmount) && // 換金額が換金申請可能かどうか
      this.amountOfLivePoints >= this.cashAmount // 所持LIVEポイントが換金額以上か
    )
  }

  // 換金申請を行う資格があるかどうか
  get canApplyForRedemption(): boolean {
    return (
      this.wiseAccountExists && // wise用アカウント情報が登録されているか
      this.systemCanConvertLivePoints && // システム側がLIVEポイント換金可能な状態か
      this.amountOfLivePoints >= wisePayoutMinLivePoints // 所持LIVEポイントが最低換金可能額以上か
    )
  }

  // 換金額（Number型）
  get cashAmount(): number {
    const amount = Number(this.inputCashAmount)
    if (isNaN(amount) || !Number.isInteger(amount) || amount < 0) {
      return 0
    }
    return amount
  }

  // 送金確認ダイアログ
  get cashConfirmDialogTitle(): string {
    if (this.preferredCurrency === '') {
      this.preferredCurrency = wiseDefaultPreferredCurrency
    }
    return this.$t('livePointPayoutConfirmationMessage', {
      dollars: this.resultCash,
      livePoints: this.formatter.format(this.cashAmount),
    }) as string
  }

  // 画面を読み込んだ時点で表示するエラーメッセージ
  get errorMessage(): string {
    // wiseアカウント未登録の場合
    if (!this.wiseAccountExists) {
      return this.$t('personalInfoUnregistered') as string
    }

    // LIVEポイント集計中の場合
    if (!this.systemCanConvertLivePoints) {
      return this.$t('livePointsAggregationNotice') as string
    }

    // LIVEポイントが最低換金可能額未満の場合
    if (this.amountOfLivePoints < wisePayoutMinLivePoints) {
      return this.$t('livePointsBelowMinRedemption', {
        minLivePoints: wisePayoutMinLivePoints,
      }) as string
    }

    // 所持LIVEポイントが不足している場合
    if (this.amountOfLivePoints < this.cashAmount) {
      return this.$t('inputValidationMessageInsufficientLivePoints') as string
    }

    // 入力換金額が最低換金可能額未満の場合
    if (
      this.inputCashAmount !== '' &&
      this.cashAmount < wisePayoutMinLivePoints
    ) {
      return this.$t('livePointsBelowMinRedemption', {
        minLivePoints: wisePayoutMinLivePoints,
      }) as string
    }

    // 入力換金額が最大換金可能額を超えている場合
    if (
      this.inputCashAmount !== '' &&
      this.cashAmount > wisePayoutMaxLivePoints
    ) {
      return this.$t('inputValidationMessageLivePointsAtMaxRedemptionLimit', {
        maxLivePoints: wisePayoutMaxLivePoints,
      }) as string
    }

    return ''
  }

  // LIVEポイント換金可能かどうか
  canCash(amountOfLivePoints: number): boolean {
    return new WiseService().canCash(amountOfLivePoints)
  }

  async fetchStreamerUser(): Promise<void> {
    try {
      const res = await api().streamer.owner.get({ isLatestCounting: false })
      this.user = res.streamer
    } catch (e) {
      console.error(e) // eslint-disable-line
      alert(this.$t('errorGeneral'))
    }
  }

  async verifyWiseAccount() {
    try {
      this.wiseAccountExists = false
      const res = await apiV2().wiseAccount.get()
      if (
        res.firstName != '' &&
        res.lastName != '' &&
        res.email != '' &&
        res.region != '' &&
        res.canCashTransfer // 送金申請可能なアカウントかどうか
      ) {
        this.wiseAccountExists = true
        return
      }
      return
    } catch (e) {
      console.error(e) // eslint-disable-line
      alert(this.$t('errorGeneral'))
    }
  }

  async fetchBalanceOfLivePoints(): Promise<void> {
    try {
      const response = await api().streamer.currency.live_point.balance()
      this.systemCanConvertLivePoints = response.canConvert
      this.amountOfLivePoints = response.livePoint.total
    } catch (error) {
      console.error(error) // eslint-disable-line
    }
  }

  async fetchRate(): Promise<void> {
    try {
      const response = await apiV2().wise.rate.get()
      const rate = response.rateOfUsd
      this.rateOfUSD = new Big(rate)

      if (response.nextRateOfUsd) {
        this.nextRateOfUSD = new Big(response.nextRateOfUsd)
        this.nextApplicationBeginDate = dayjs(
          response.nextApplicationBeginDate
        ).tz('Asia/Tokyo')
      }
    } catch (error) {
      console.error(error) // eslint-disable-line
    }
  }

  onClickCashButton(): void {
    if (!this.userCanSubmitRequest) {
      return
    }
    this.cashConfirmDialogIsActive = true
  }

  async onFinishCashConfirmDialog(result: { ok: boolean }): Promise<void> {
    this.cashConfirmDialogIsActive = false
    if (!result.ok) {
      return
    }
    // ボタンを連打させない
    if (this.isRequestingCash) {
      return
    }
    if (this.rateOfUSD == null) {
      return
    }
    if (this.canCash(this.cashAmount) === false) {
      return
    }

    this.isRequestingCash = true
    let success = false
    try {
      // 送金処理
      await apiV2().wise.payout.request({
        AmountOfLivePoints: this.cashAmount,
        RateOfUSD: this.rateOfUSD.toString(),
        PreferredCurrency:
          this.preferredCurrency !== ''
            ? this.preferredCurrency
            : wiseDefaultPreferredCurrency, // 空の場合デフォルト通貨のUSDをセット
      })
      success = true
    } catch (error) {
      console.error(error) // eslint-disable-line
      this.cashFailureDialogIsActive = true
    } finally {
      this.isRequestingCash = false
    }
    if (success) {
      // 送金申請成功時は送金申請完了画面に遷移
      this.$router.push('/mypage/payment_registration/wise/payout/success').catch(() => {})
    }
  }

  onFinishCashFailureDialog(result: { ok: boolean }): void {
    this.cashFailureDialogIsActive = false
    if (result.ok) {
      // 問い合わせページに遷移する
      location.href =
        'https://help.le.wrightflyer.net/?action=inquiry_form&app_id=258305124483241'
    }
  }
}
</script>

<style scoped>
#mypage__wrapper {
  position: relative;
  height: auto;
  min-height: 100%;
  background-color: #fafafa;
}

#mypage__container {
  font-family: sans-serif;
  font-size: 14px;
  line-height: 1.43;
  color: #262626;
  padding-bottom: 80px;
}

section {
  margin-top: 20px;
}

.error__wrapper {
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 24px;
}

.error__container {
  color: #ff003d;
  font-size: 12px;
  min-height: 17px;
  font-family: 'Hiragino Kaku Gothic ProN';
  font-style: normal;
  font-weight: 700;
}

.notes__wrapper {
  padding-left: 16px;
  padding-right: 16px;
}

.notes__container {
  font-size: 12px;
  line-height: 16.8px;
  font-family: 'Hiragino Kaku Gothic ProN';
  color: #808080;
}

.live_point_icon {
  padding-right: 4px;
}

.button__wrapper {
  display: flex;
  justify-content: center;
}

.button__container {
  display: inline-block;
  width: calc(100% - 16px * 2);
}

.payout-form__container {
  padding-bottom: 16px;
}

.list__container {
  padding-bottom: 17px;
}

.list__container--disabled {
  opacity: 0.5;
}

.list__description {
  font-size: 12px;
  line-height: 20px;
  color: #808080;
  padding-left: 16px;
  padding-bottom: 10px;
}

.list__amount {
  font-family: 'Hiragino Kaku Gothic ProN';
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 140%;
}

.cash-amount {
  font-size: 14px;
  color: #262626;
  text-align: right;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 0;
  outline: none;
  background: none;
  line-height: 1.43;
}
</style>
